import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FileText, TrendingUp, AlertCircle, CheckCircle, Clock, Shield } from 'lucide-react';

const f = "'Inter',ui-sans-serif,system-ui,sans-serif";
const card = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 16, boxShadow: '0 1px 2px rgba(15,23,42,0.04)' };

function StatCard({ label, value, sub, Icon }) {
  return (
    <div style={{ ...card, padding: '20px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 13, color: '#64748B', fontWeight: 500 }}>{label}</span>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color="#94A3B8" />
        </div>
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: '#1E293B', lineHeight: 1, marginBottom: 6 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#94A3B8' }}>{sub}</div>
    </div>
  );
}

export default function BoardDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem('hcmsToken') || sessionStorage.getItem('token') || localStorage.getItem('hcmsToken') || localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };

        // Fetch board-level stats
        const statsRes = await fetch('/api/admin/dashboard/stats', { headers });
        if (!statsRes.ok) throw new Error('Failed to fetch stats');
        const statsData = await statsRes.json();

        setStats(statsData);
      } catch (err) {
        console.error('Error fetching board dashboard data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div style={{ fontFamily: f, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: '#64748B' }}>Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: f, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <AlertCircle size={48} color="#EF4444" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 14, color: '#EF4444', marginBottom: 8 }}>Error loading dashboard</div>
          <div style={{ fontSize: 12, color: '#94A3B8' }}>{error}</div>
        </div>
      </div>
    );
  }

  const dashboardStats = [
    { label: 'Total Cases', value: formatNumber(Object.values(stats?.tickets_by_status || {}).reduce((a, b) => a + b, 0)), sub: 'All cases across organization', Icon: FileText },
    { label: 'Open Cases', value: formatNumber(stats?.tickets_by_status?.new || 0), sub: 'Awaiting action', Icon: Clock },
    { label: 'Escalated Cases', value: formatNumber(stats?.tickets_by_status?.escalated || 0), sub: 'Requiring board attention', Icon: AlertCircle },
    { label: 'Resolved Cases', value: formatNumber(stats?.tickets_by_status?.resolved || 0), sub: 'Successfully resolved', Icon: CheckCircle },
    { label: 'Total Users', value: formatNumber(Object.values(stats?.users || {}).reduce((a, b) => a + b, 0)), sub: 'All employees and staff', Icon: Users },
    { label: 'Departments', value: formatNumber(stats?.departments || 0), sub: 'Active departments', Icon: Shield },
  ];

  return (
    <div style={{ fontFamily: f }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1E293B', margin: '0 0 8px' }}>Board Dashboard</h1>
        <p style={{ fontSize: 14, color: '#64748B', margin: 0 }}>Organization-wide overview and metrics</p>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {dashboardStats.map(s => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Quick Actions */}
      <div style={{ ...card, padding: '20px 24px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: '#1E293B', margin: '0 0 16px' }}>Quick Actions</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button 
            onClick={() => navigate('/hcms/tickets')}
            style={{ height: 36, padding: '0 16px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#FFFFFF', fontSize: 13, fontWeight: 600, color: '#64748B', cursor: 'pointer', fontFamily: f }}
          >
            View All Cases
          </button>
          <button 
            onClick={() => navigate('/hcms/escalations')}
            style={{ height: 36, padding: '0 16px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#FFFFFF', fontSize: 13, fontWeight: 600, color: '#64748B', cursor: 'pointer', fontFamily: f }}
          >
            View Escalations
          </button>
        </div>
      </div>
    </div>
  );
}
